import LockPermissionTooltip from 'components/common/LockPermissionTooltip'
import PermissionIcon from 'components/common/PermissionIcon'
import {KNOWLEDGEBASE_PATH} from 'constants/path'
import useKnowledgebase from 'hooks/pure/useKnowledgebase'
import useUserPermissions from 'hooks/pure/useUserPermission'
import React, {useState, useMemo, useCallback} from 'react'
import {
  FiChevronDown,
  FiChevronRight,
  FiCode,
  FiDatabase,
  FiDownload,
  FiFileText,
  FiGitBranch,
  FiGlobe,
  FiLayers,
  FiPenTool,
  FiSettings,
  FiShuffle,
  FiZap,
} from 'react-icons/fi'
import {useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {get, startCase} from 'utils/helper'
import {
  VmoButton,
  VmoMenu,
  VmoMenuHeader,
  VmoMenuItem,
  VmoAccordion,
  VmoAccordionContent,
  VmoAccordionTitle,
  VmoIcon,
} from 'vmo-library'

const getHref = docDetails => {
  const url = docDetails?.url
  const accountinfo = get(['accountInfo'], JSON.parse(window.localStorage.getItem('support')))

  if (docDetails?.cname?.length) return `http://${docDetails.cname}/${url}`

  return `https://${accountinfo.domain}.${process.env.REACT_APP_DOMAIN}/hc/support/${url}`
}

const KBMenu = ({iscreateModalOpen, setIsCreateModalOpen}) => {
  const navigate = useNavigate()
  const {knowledgeBaseId, knowledgeBaseName} = useKnowledgebase()

  const [isSettingOpen, setIsSettingOpen] = useState(true)

  const {kbData = [], docDetails = {}} = useSelector(state => state.knowledgebase)

  const {knowledgeBaseAccess, docsPermissions} = useUserPermissions()

  const allowCustomizeDocsRole = docsPermissions?.customize_docs
  const menuItems = useMemo(
    () => [
      {
        label: 'Articles',
        icon: <FiFileText />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.ARTICLES}`,
        key: 'articles',
      },
      {
        label: 'Categories',
        icon: <FiLayers />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.CATEGORIES}`,
        key: 'categories',
      },
      {
        label: 'Customization',
        icon: <FiPenTool />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.CUSTOMIZATION}`,
        key: 'customization',
      },
    ],
    [knowledgeBaseId]
  )
  const settingItems = useMemo(
    () => [
      {
        label: 'General',
        icon: <FiSettings />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.GENERAL}`,
        key: 'general',
      },
      {
        label: 'Advanced',
        icon: <FiZap />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.ADVANCED}`,
        key: 'advance',
      },
      {
        label: 'SEO',
        icon: <FiGlobe />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.SEO}`,
        key: 'seo',
      },
      {
        label: 'Version Control',
        icon: <FiGitBranch />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.VERSION_CONTROL}`,
        key: 'versionControl',
        permission: knowledgeBaseAccess?.versionControl,
      },
      {
        label: 'Backup & Restore',
        icon: <FiDatabase />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.BACKUP_AND_RESTORE}`,
        key: 'backupRestore',
        permission: knowledgeBaseAccess?.backupRestore,
      },
      {
        label: 'Custom CSS & JS',
        icon: <FiCode />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.CUSTOM_CSS_JS}`,
        key: 'customCssJs',
        permission: knowledgeBaseAccess?.customCSSJavascript,
      },
      {
        label: 'Redirect Rules',
        icon: <FiShuffle />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.REDIRECT_RULES}`,
        key: 'redirectRules',
      },
      {
        label: 'Import & Export',
        icon: <FiDownload />,
        path: `/${KNOWLEDGEBASE_PATH.PREFIX}/${knowledgeBaseId}/${KNOWLEDGEBASE_PATH.IMPORT_EXPORT}`,
        key: 'import',
        permission: knowledgeBaseAccess?.importExport,
      },
    ],
    [knowledgeBaseAccess, knowledgeBaseId]
  )

  const handleNavigation = useCallback(
    path => {
      navigate(path)
    },
    [navigate]
  )

  const renderAnotherDocOption = () => {
    if (kbData.length === 1)
      return (
        <LockPermissionTooltip isRoleAccessDenied={!allowCustomizeDocsRole}>
          <VmoButton
            className="basic-fill-btn createKwBtn"
            fluid
            onClick={() => allowCustomizeDocsRole && setIsCreateModalOpen(!iscreateModalOpen)}
          >
            Create Knowledgebase
          </VmoButton>
        </LockPermissionTooltip>
      )
  }

  return (
    <VmoMenu vertical secondary className="set-list" style={{padding: '0'}}>
      <div
        style={{
          ...(kbData.length === 1
            ? {height: 'calc(100vh - 152px)', overflowY: 'auto', padding: '14px 10px'}
            : {padding: '14px 10px'}),
        }}
      >
        <VmoMenuHeader style={{marginBottom: '0.5em'}}>{startCase(knowledgeBaseName)}</VmoMenuHeader>

        {menuItems.map(item => (
          <VmoMenuItem
            key={item.key}
            className={`${item.key}Tool`}
            active={window.location.pathname.match(`/${KNOWLEDGEBASE_PATH.PREFIX}/[0-9]+/${item.key}`)}
            onClick={() => handleNavigation(item.path)}
          >
            {item.icon} {item.label}
          </VmoMenuItem>
        ))}

        <VmoAccordion>
          <VmoAccordionTitle className="SettingTool" onClick={() => setIsSettingOpen(!isSettingOpen)}>
            Settings
            {isSettingOpen ? <FiChevronDown /> : <FiChevronRight />}
          </VmoAccordionTitle>
          <VmoAccordionContent active={isSettingOpen}>
            {settingItems.map(item => (
              <VmoMenuItem
                key={item.key}
                active={window.location.pathname.match(item.path)}
                onClick={() => handleNavigation(item.path)}
              >
                {item.icon} {item.label}
                {item.permission === false && (
                  <span>
                    <PermissionIcon isFeatureAccessDenied={!item.permission} />
                  </span>
                )}
              </VmoMenuItem>
            ))}
          </VmoAccordionContent>
        </VmoAccordion>
      </div>

      <div className="prevBottom">
        {renderAnotherDocOption()}
        <VmoButton
          fluid
          className="basic-fill-btn PreviewTool"
          href={`${getHref(docDetails)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <VmoIcon name="external alternate" /> Preview
        </VmoButton>
      </div>
    </VmoMenu>
  )
}

export default KBMenu
